import type {
  BillingInterval,
  BillingOverview,
  CompanyInvoiceList,
  InvoiceLinks,
  PaymentMethodResponse,
  PlanChangePreview,
  PlanId,
  SuperBillingList,
  SuperBillingListRequest,
  SuperBillingView
} from '@tandemone/types'

import { api } from '@/api/client'

export type ChangePlanResult = {
  applied: 'immediate' | 'scheduled' | 'schedule_released' | 'unchanged'
  effectiveAt: string | null
}

export async function getBillingOverview(
  signal?: AbortSignal
): Promise<BillingOverview> {
  const { data } = await api.get<BillingOverview>('/v1/billing/overview', {
    signal
  })
  return data
}

/* invoice customers pay in a second tab so this page stays mounted for the
 * paid broadcast. */
export type StartPurchaseResult = {
  url: string
  mode: 'checkout' | 'invoice'
}

export async function createCheckoutSession(
  planId: PlanId,
  interval: BillingInterval
): Promise<StartPurchaseResult> {
  const { data } = await api.post<StartPurchaseResult>('/v1/billing/checkout', {
    planId,
    interval
  })
  return data
}

export type PlanIntentResult = {
  url: string | null
  mode: 'setup' | 'saved' | 'invoice' | 'free'
}

export async function savePlanIntent(
  planId: PlanId,
  interval: BillingInterval
): Promise<PlanIntentResult> {
  const { data } = await api.post<PlanIntentResult>('/v1/billing/plan-intent', {
    planId,
    interval
  })
  return data
}

/* the server checks the session belongs to this company before recording, so
 * the page never takes a query param's word for it. */
export async function confirmPlanIntent(
  sessionId: string
): Promise<{ planId: PlanId; interval: BillingInterval }> {
  const { data } = await api.post<{
    planId: PlanId
    interval: BillingInterval
  }>('/v1/billing/plan-intent/confirm', { sessionId })
  return data
}

export async function changePlan(
  planId: PlanId,
  interval: BillingInterval
): Promise<ChangePlanResult> {
  const { data } = await api.post<ChangePlanResult>('/v1/billing/change-plan', {
    planId,
    interval
  })
  return data
}

export async function getPlanChangePreview(
  planId: PlanId,
  interval: BillingInterval,
  signal?: AbortSignal
): Promise<PlanChangePreview> {
  const { data } = await api.get<PlanChangePreview>(
    '/v1/billing/change-plan/preview',
    { params: { planId, interval }, signal }
  )
  return data
}

export async function cancelSubscription(): Promise<void> {
  await api.post('/v1/billing/cancel')
}

export async function resumeSubscription(): Promise<void> {
  await api.post('/v1/billing/resume')
}

export async function getInvoices(
  page: number,
  pageSize: number,
  signal?: AbortSignal
): Promise<CompanyInvoiceList> {
  const { data } = await api.get<CompanyInvoiceList>('/v1/billing/invoices', {
    params: { page, pageSize },
    signal
  })
  return data
}

/* minted per click: a stored link would be dead by the time it is used. */
export async function getInvoiceLinks(
  invoiceId: string
): Promise<InvoiceLinks> {
  const { data } = await api.get<InvoiceLinks>(
    `/v1/billing/invoices/${invoiceId}/links`
  )
  return data
}

export async function getPaymentMethod(
  signal?: AbortSignal
): Promise<PaymentMethodResponse> {
  const { data } = await api.get<PaymentMethodResponse>(
    '/v1/billing/payment-method',
    { signal }
  )
  return data
}

export async function createPortalSession(): Promise<{ url: string }> {
  const { data } = await api.post<{ url: string }>('/v1/billing/portal')
  return data
}

export async function getSuperBilling(
  companyId: string,
  signal?: AbortSignal
): Promise<SuperBillingView> {
  const { data } = await api.get<SuperBillingView>(
    `/v1/super/billing/${companyId}`,
    { signal }
  )
  return data
}

export async function getSuperBillingBySlug(
  slug: string,
  signal?: AbortSignal
): Promise<SuperBillingView> {
  const { data } = await api.get<SuperBillingView>(
    `/v1/super/billing/by-slug/${slug}`,
    { signal }
  )
  return data
}

export async function getSuperInvoices(
  companyId: string,
  page = 1,
  pageSize = 20,
  signal?: AbortSignal
): Promise<CompanyInvoiceList> {
  const { data } = await api.get<CompanyInvoiceList>(
    `/v1/super/billing/${companyId}/invoices`,
    { params: { page, pageSize }, signal }
  )
  return data
}

export async function getSuperBillingList(
  opts: SuperBillingListRequest,
  signal?: AbortSignal
): Promise<SuperBillingList> {
  const { data } = await api.get<SuperBillingList>('/v1/super/billing', {
    params: opts,
    signal
  })
  return data
}

export async function getSuperInvoiceLinks(
  companyId: string,
  invoiceId: string
): Promise<InvoiceLinks> {
  const { data } = await api.get<InvoiceLinks>(
    `/v1/super/billing/${companyId}/invoices/${invoiceId}/links`
  )
  return data
}
